import { vec3 } from 'gl-matrix';
import type { PlaneMm } from '~/domain/geometry/patientGeometry';
import {
    DicomContour,
    ContourGeometricType,
} from '~/domain/dicom/dicomstructureset';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { SignedDistanceField } from '~/engine/core/models/SignedDistanceField';
import { ContourSlice } from '~/engine/data/structure/ContourSlice';
import { Contour } from '~/engine/data/structure/Contour';
import { ContourSequence } from '~/engine/data/structure/ContourSequence';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '~/engine/tool/EventHandlerResult';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { resolveTextureSlice } from '~/engine/data/structure/planeSliceResolution';
import { Modality } from '~/domain/dicom/modality';
import { SDF_ISO_THRESHOLD } from './sdfConstants';
import {
    estimateRobustIntensitySpread,
    extractIntensitySliceAlignedToGrid,
    refineSliceWithIntensityEdge,
    refineSliceWithIntensityRegion,
    scaleRefinementOptionsForSpread,
    type EdgeRefinementOptions,
    type RegionRefinementOptions,
} from './IntensityRefinement';

/**
 * Opt-in configuration for image-intensity-aware (intensity-guided)
 * interpolation. When {@link enabled} is `false` (the default),
 * interpolation is the historical pure shape-only B-spline/linear
 * blend and is byte-for-byte identical to the pre-refinement
 * behaviour — the refinement code path is never entered.
 *
 * When enabled, each interpolated slice is passed through the shared
 * {@link refineSliceWithIntensityEdge} (edge/gradient) pass and the
 * {@link refineSliceWithIntensityRegion} (absolute-intensity region)
 * pass. Both passes run for every modality: the intensity-unit
 * thresholds are scaled per modality from the slice's own robust
 * intensity spread (CT keeps its calibrated HU thresholds), so the
 * refinement adapts to PET / MR ranges instead of being CT-gated —
 * matching the contour auto-suggest tool. The refinement is a strictly
 * local, clamped, topology-preserving post-correction — never a
 * re-segmentation — and degrades to the unmodified shape-only slice
 * whenever intensity is unavailable, the grids are not voxel-aligned,
 * confidence is low, or a non-CT slice's intensity spread cannot be
 * characterised.
 */
export type IntensityInterpolationOptions = {
    /** Master opt-in. Defaults to `false` (shape-only interpolation). */
    enabled: boolean;
    /** Optional per-call tuning for the edge/gradient pass. */
    edgeOptions?: Partial<EdgeRefinementOptions>;
    /** Optional per-call tuning for the absolute-intensity region pass. */
    regionOptions?: Partial<RegionRefinementOptions>;
};

const DISABLED_INTENSITY_INTERPOLATION: Readonly<IntensityInterpolationOptions> =
    {
        enabled: false,
    };

/**
 * Derive modality-aware overrides for the two intensity-refinement
 * passes, scaling the intensity-unit thresholds so the CT-tuned
 * refinement also works on PET / MR:
 *
 *   - CT (calibrated Hounsfield Units): use the fixed, physically
 *     tuned defaults → empty overrides.
 *   - Non-CT (PET activity/SUV, MR arbitrary units): delegate to the
 *     shared {@link scaleRefinementOptionsForSpread} so the scaling
 *     formula stays identical to the contour auto-suggest tool.
 *
 * Returns `undefined` when a non-CT slice's intensity spread cannot be
 * characterised, signalling the caller to skip refinement (keep the
 * shape-only blend) rather than chase noise.
 */
function deriveScaledRefinementOptions(
    intensitySlice: Float32Array,
    modality: Modality | undefined
):
    | {
          edge: Partial<EdgeRefinementOptions>;
          region: Partial<RegionRefinementOptions>;
      }
    | undefined {
    // CT is calibrated in HU — the defaults are physically tuned.
    if (modality === undefined || modality === Modality.CT) {
        return { edge: {}, region: {} };
    }

    const spread = estimateRobustIntensitySpread(intensitySlice);
    if (spread === undefined) {
        return undefined;
    }
    return scaleRefinementOptionsForSpread(spread);
}

/**
 * Read-only description of what an interpolation operation would do, computed
 * without writing to the SDF. Distances are in millimetres (the domain unit);
 * the UI layer converts to centimetres for display. Used to show the user
 * which slices will be filled before they commit (SR-WOR-H-24-1).
 *
 * - `fills`        — at least one empty slice will be filled; `sliceCount` is
 *                    how many, `startZMm`/`endZMm` bound their patient-Z span
 *                    (ordered so `startZMm <= endZMm`, independent of grid
 *                    axis direction). `mode` says how the fill is produced so
 *                    the read-out can be honest: `interpolate` when the
 *                    slice(s) lie between two contours and are blended from
 *                    both; `copy` (single-slice only) when the viewed slice
 *                    has a contoured neighbour on just one side, so that
 *                    neighbour's contour is copied onto it (extrapolation).
 * - `nothingToFill`— no empty slice lies between two contoured slices.
 * - `alreadyContoured` — (single-slice) the viewed slice already has a contour.
 * - `noNeighbors`  — (single-slice) the viewed slice is empty but has no
 *                    contoured slice above or below to interpolate from.
 * - `outOfRange`   — (single-slice) the viewed plane resolves outside the grid.
 * - `notASlicePlane` — (single-slice) the viewed plane coincides with no
 *                    image slice plane — a rotated view, or a cut across an
 *                    obliquely acquired grid — so no single slice is "the
 *                    current one".
 * - `unavailable`  — the ROI has no SDF texture yet.
 */
export type InterpolationPreview =
    | {
          status: 'fills';
          mode: 'interpolate' | 'copy';
          sliceCount: number;
          startZMm: number;
          endZMm: number;
      }
    | { status: 'nothingToFill' }
    | { status: 'alreadyContoured' }
    | { status: 'noNeighbors' }
    | { status: 'outOfRange' }
    | { status: 'notASlicePlane' }
    | { status: 'unavailable' };

/**
 * Where a texture slice lies relative to a structure's contoured range
 * [zLo, zHi], its lowest and highest contoured slice in texture-Z order.
 * `nearestZ` is zLo below the range and zHi above it.
 */
export type ContouredRangePosition =
    | { readonly kind: 'noContour' | 'onContour' | 'between' }
    | { readonly kind: 'beyond'; readonly nearestZ: number };

/**
 * Contour interpolation and related SDF slice operations. The interpolation
 * algorithm — shape-only SDF blend, one-sided extrapolation, the opt-in
 * image-guided refinement, preview/apply parity, and the post-apply gap
 * verification — is documented in `docs/interpolation_algorithm.md`.
 */
export class ContourOperations {
    /**
     * Whether any voxel in the slice marks it as already contoured, tested at
     * the canonical SDF iso-value so this predicate agrees with every other
     * consumer of the field about what "drawn" means — the renderer, the
     * marching-squares extraction that writes the DICOM contour, the volume
     * calculation and {@link SignedDistanceField.getContentBounds} all treat
     * a byte at or above the iso as inside. The interpolation write paths,
     * the pre-apply preview and the post-apply verification share this one
     * predicate, so they cannot disagree about which slices are drawn.
     */
    public static sliceHasContour(slice: Uint8Array): boolean {
        // Negation of the SDF's own single-slice emptiness scan (`>=`) —
        // reuse it rather than re-walking the bytes here.
        return !SignedDistanceField.isSliceEmpty(slice, SDF_ISO_THRESHOLD);
    }

    /**
     * A reader of whether slice `z` of `sdf` holds contour: from the pixel
     * cache where it is populated, which costs no readback, and from the
     * GPU otherwise.
     */
    public static contourPresenceReader(
        sdf: SignedDistanceField
    ): (z: number) => boolean {
        const cached = sdf.hasCachedPixels ? sdf.pixels : undefined;
        return (z) =>
            ContourOperations.sliceHasContour(
                cached?.[z] ?? sdf.sliceToArray(z)
            );
    }

    /**
     * Locates texture slice `targetZ` — which may lie outside the texture,
     * where it holds no contour — against the contoured range of a texture
     * `sizeZ` slices deep. Each end of the range is found walking inward
     * from the texture edge on its side, so a gap inside the range is
     * never read.
     */
    public static locateInContouredRange(
        targetZ: number,
        sizeZ: number,
        hasContour: (z: number) => boolean
    ): ContouredRangePosition {
        if (targetZ >= 0 && targetZ < sizeZ && hasContour(targetZ)) {
            return { kind: 'onContour' };
        }
        const [below, above] = [
            Math.min(targetZ, sizeZ) - 1,
            Math.max(targetZ, -1) + 1,
        ];
        const first = ContourOperations.firstContouredSlice;
        const hasBelow = first(0, below, 1, hasContour) !== undefined;
        const hasAbove = first(sizeZ - 1, above, -1, hasContour) !== undefined;
        if (hasBelow === hasAbove) {
            return { kind: hasBelow ? 'between' : 'noContour' };
        }
        // All contour lies on one side: the nearest slice there is the
        // range end.
        const nearestZ = hasBelow
            ? first(below, 0, -1, hasContour)
            : first(above, sizeZ - 1, 1, hasContour);
        return { kind: 'beyond', nearestZ: nearestZ! };
    }

    /**
     * The first slice holding contour walking by `step` from `fromZ` to
     * `toZ`, both inclusive; none when `toZ` lies behind `fromZ`.
     */
    private static firstContouredSlice(
        fromZ: number,
        toZ: number,
        step: 1 | -1,
        hasContour: (z: number) => boolean
    ): number | undefined {
        for (let z = fromZ; (toZ - z) * step >= 0; z += step) {
            if (hasContour(z)) {
                return z;
            }
        }
        return undefined;
    }

    public polygonToContours(polygonMm: readonly vec3[]): Contour[] {
        const roiContours: DicomContour[] = [];

        const contour = new DicomContour(
            ContourGeometricType.CLOSED_PLANAR,
            polygonMm,
            '',
            ''
        );
        roiContours.push(contour);

        return roiContours.map(
            (contour) => new Contour(contour.data as vec3[])
        );
    }

    public polygonToSdf(
        polygonMm: readonly vec3[],
        targetImage: ImageObject
    ): ContourTexture {
        const contours = this.polygonToContours(polygonMm);

        if (contours.length === 0 || contours[0].vertices.length === 0) {
            return ContourTexture.createEmpty(targetImage.parent.grid);
        }

        const normalDir = targetImage.parent.grid.getUnitZ();
        const normalPosition = vec3.dot(normalDir, contours[0].vertices[0]);

        const contourSequence = new ContourSequence(targetImage.parent.grid, [
            new ContourSlice(normalPosition, contours),
        ]);

        const { texture, renderErrors } = ContourTexture.fromContourSequence(
            targetImage.parent.grid,
            contourSequence
        );

        if (renderErrors) {
            throw new Error(
                `Failed to generate SDF from polygon: ${renderErrors}`
            );
        }

        return texture;
    }

    /**
     * Intensity-guided refinement of a single interpolated SDF slice.
     *
     * This is the orthogonal post-pass that sits between computing an
     * interpolated slice (linear/B-spline shape blend) and writing it
     * back to the texture. It delegates the actual pixel work to the
     * shared, pure {@link IntensityRefinement} module so the contour
     * auto-suggest tool and the Interpolation tool share one
     * implementation.
     *
     * Fail-safe contract: returns the **same** {@link sliceVoxels}
     * reference unchanged (no copy, no write) whenever refinement is
     * not applicable — no texture grid, no float intensity buffer, or
     * the grids are not voxel-aligned / the Z is out of volume
     * (signalled by {@link extractIntensitySliceAlignedToGrid}
     * returning `undefined`). Callers use the returned identity to skip
     * a redundant GPU write.
     *
     * @param target ROI being interpolated (provides the texture grid).
     * @param image Active image supplying the float intensity buffer + grid.
     * @param curZ Texture-voxel Z index of the slice being refined.
     * @param sliceVoxels Shape-only interpolated SDF slice (read-only).
     * @param options Tuning for the edge / region passes.
     */
    private refineInterpolatedSlice(
        target: RegionOfInterest,
        image: ImageObject,
        curZ: number,
        sliceVoxels: Uint8Array,
        options: IntensityInterpolationOptions
    ): Uint8Array {
        const texture = target.texture;
        const textureGrid = texture?.textureGrid;
        const parent = image.parent;
        const floatData = parent?.floatPixelData;
        if (!texture || !textureGrid || !floatData) {
            return sliceVoxels;
        }

        // Sample the image's float intensity buffer onto the structure's
        // texture grid. Returns undefined for registered/oblique grids
        // (not voxel-aligned) or when curZ is outside the image volume;
        // in either case we keep the deterministic shape-only result.
        const intensitySlice = extractIntensitySliceAlignedToGrid(
            textureGrid,
            parent.grid,
            floatData,
            curZ
        );
        if (!intensitySlice) {
            return sliceVoxels;
        }

        const { sizeX, sizeY } = textureGrid;

        // SDF byte-distance units per physical mm for this texture's
        // encoding. The mm-based band / max-shift options are converted
        // to byte units with this factor inside the refinement passes —
        // omitting it (passing the options object in its place) makes
        // the band/clamp NaN and scatters the contour.
        const sdfBytesPerMm = texture.sdfBytesPerMm();

        // Modality-aware threshold scaling so the CT-tuned refinement
        // works across CT / MR / PET (mirrors the contour auto-suggest
        // tool). CT keeps its calibrated HU thresholds; other modalities
        // scale the intensity-unit thresholds by the slice's own robust
        // intensity spread. A non-CT slice whose spread cannot be
        // characterised skips refinement entirely so we keep the
        // deterministic shape-only result.
        const scaled = deriveScaledRefinementOptions(
            intensitySlice,
            parent.firstSlice.modality
        );
        if (!scaled) {
            return sliceVoxels;
        }

        // Pass 1 — edge/gradient: pull the iso toward strong intensity
        // gradients (anatomical boundaries). Runs for every modality;
        // an intensity edge is a meaningful boundary cue on CT, MR and
        // PET alike. Caller overrides take precedence over the
        // per-modality scaled thresholds.
        let refined = refineSliceWithIntensityEdge(
            sliceVoxels,
            intensitySlice,
            sizeX,
            sizeY,
            sdfBytesPerMm,
            {
                ...scaled.edge,
                ...options.edgeOptions,
            }
        );

        // Pass 2 — region/absolute-intensity: contract/expand where the
        // absolute value clearly matches interior vs exterior (e.g. air
        // ≈ −1000 inside a lung vs tissue ≈ 0 on CT; high uptake vs
        // background on PET). Also runs for every modality — the
        // intensity-unit thresholds were scaled per modality above, so
        // the pass adapts to PET / MR ranges instead of being CT-gated.
        refined = refineSliceWithIntensityRegion(
            refined,
            intensitySlice,
            sizeX,
            sizeY,
            sdfBytesPerMm,
            {
                ...scaled.region,
                ...options.regionOptions,
            }
        );

        return refined;
    }

    /**
     * Run the intensity-guided refinement post-pass over the given filled
     * slice indices, writing refined bytes back into {@link sdf}. Each slice
     * is refined independently from its own intensity data — never from
     * another refined slice — so call order does not matter.
     *
     * No-op when {@link options} is disabled. Identity returns from
     * {@link refineInterpolatedSlice} (refinement not applicable) skip the
     * redundant GPU write, matching the previous inline behaviour. Callers
     * that may lack an image (the all-slices path) must gate on it first.
     */
    private applyRefinementPass(
        sdf: SignedDistanceField,
        target: RegionOfInterest,
        image: ImageObject,
        filledZs: number[],
        options: IntensityInterpolationOptions
    ): void {
        if (!options.enabled) {
            return;
        }
        for (const z of filledZs) {
            const shapeOnly = sdf.sliceToArray(z);
            const refined = this.refineInterpolatedSlice(
                target,
                image,
                z,
                shapeOnly,
                options
            );
            if (refined !== shapeOnly) {
                sdf.arrayToTextureSlice(refined, z);
            }
        }
    }

    /*
     *
     * Interpolates the current slice of the SDF texture.
     * @param target - The region of interest to interpolate.
     * @param primaryImage - The image object providing the grid used to snap
     *     the viewed plane to the visible image slice.
     * @param plane - The plane the user is viewing. A plane that coincides
     *     with no image slice plane is not handled (see
     *     {@link previewInterpolateSingleSlice}).
     * @param refineOptions - Opt-in intensity-guided refinement config. Defaults to
     *     disabled, in which case the result is the historical shape-only
     *     interpolation, byte-for-byte unchanged.
     * @returns The event handler result.
     *
     * */
    public handleInterpolateSingleSlice = (
        target: RegionOfInterest,
        primaryImage: ImageObject,
        plane: PlaneMm,
        refineOptions: IntensityInterpolationOptions = DISABLED_INTENSITY_INTERPOLATION
    ): EventHandlerResult => {
        const sourceSdf = target.texture?.signedDistanceField;
        if (!target.texture || !sourceSdf) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED,
                false,
                {
                    layers: LayerBit.CONTOUR_LAYER_BIT,
                    volume: target.boundingBox,
                }
            );
        }
        // Either refusal — a plane that cuts no image slice, or one whose
        // slice this ROI's texture does not cover — leaves nothing to fill.
        const resolved = resolveTextureSlice(
            target.texture,
            primaryImage,
            plane
        );
        if (resolved.kind !== 'slice') {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED,
                false,
                {
                    layers: LayerBit.CONTOUR_LAYER_BIT,
                    volume: target.boundingBox,
                }
            );
        }
        const curZ = resolved.sliceZ;
        const roiModify = target.startModify('interpolation');
        const filled = ContourOperations.fillSingleSliceInto(sourceSdf, curZ);
        // Orthogonal intensity-guided post-pass (opt-in). Disabled → the
        // shape-only fill above is the result, byte-for-byte. Kept OUTSIDE
        // fillSingleSliceInto because the pre-apply preview ghost shares that
        // routine and applies the same refinement to its clone separately.
        if (filled) {
            this.applyRefinementPass(
                sourceSdf,
                target,
                primaryImage,
                [curZ],
                refineOptions
            );
        }
        roiModify.finish();
        return new EventHandlerResult(
            filled
                ? EventHandlerResultType.EVENT_HANDLED
                : EventHandlerResultType.EVENT_NOT_HANDLED,
            filled,
            { layers: LayerBit.CONTOUR_LAYER_BIT, volume: target.boundingBox }
        );
    };

    /*
     *
     * Interpolates all empty slices of the SDF texture.
     * @param target - The region of interest to interpolate.
     * @param image - Active image supplying HU data for refinement. May be
     *     undefined; refinement is then skipped (shape-only).
     * @param refineOptions - Opt-in intensity-guided refinement config. Defaults to
     *     disabled, in which case the result is the historical shape-only
     *     interpolation, byte-for-byte unchanged.
     * @returns The event handler result.
     *
     */
    public handleInterpolateAllSlices = (
        target: RegionOfInterest,
        image?: ImageObject,
        refineOptions: IntensityInterpolationOptions = DISABLED_INTENSITY_INTERPOLATION
    ): EventHandlerResult => {
        const sourceSdf = target.texture?.signedDistanceField;
        if (!sourceSdf) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED,
                false,
                {
                    layers: LayerBit.CONTOUR_LAYER_BIT,
                    volume: target.boundingBox,
                }
            );
        }
        const roiModify = target.startModify('interpolation');
        const filledZs = ContourOperations.fillAllEmptySlicesInto(sourceSdf);

        // Second pass: intensity-guided refinement of the just-filled slices.
        //
        // Deliberately a SEPARATE pass over the filled Z indices rather than
        // refining inside the fill routine: refinement must never feed back
        // into the geometry pass (each refined slice is derived once, from
        // its own intensity slice, after every slice has been
        // shape-interpolated — endpoint-anchored, so filled slices are never
        // interpolation endpoints either way), and the shared shape-only
        // routine is also what the pre-apply preview ghost runs, which
        // applies this same refinement to its clone separately. Disabled →
        // the result is byte-for-byte the shape-only fill.
        if (image) {
            this.applyRefinementPass(
                sourceSdf,
                target,
                image,
                filledZs,
                refineOptions
            );
        }

        roiModify.finish();
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true,
            { layers: LayerBit.CONTOUR_LAYER_BIT, volume: target.boundingBox }
        );
    };

    /**
     * Linearly interpolate one intermediate slice's SDF bytes from the two
     * bounding contoured slices. Pure, and the single place the interpolation
     * arithmetic — and its rounding — is defined, so every fill path produces
     * identical bytes for the same endpoints.
     *
     * @param bottomVoxels SDF bytes of the lower contoured slice.
     * @param topVoxels SDF bytes of the upper contoured slice.
     * @param bottomZ Slice index of the lower contoured slice.
     * @param topZ Slice index of the upper contoured slice (`> bottomZ`).
     * @param curZ Slice index being interpolated (`bottomZ < curZ < topZ`).
     */
    private static interpolateSliceVoxels(
        bottomVoxels: Uint8Array,
        topVoxels: Uint8Array,
        bottomZ: number,
        topZ: number,
        curZ: number
    ): Uint8Array {
        const fractionalDistance = (curZ - bottomZ) / (topZ - bottomZ);
        return Uint8Array.from(
            bottomVoxels.map((bottomValue, i) =>
                Math.round(
                    bottomValue +
                        fractionalDistance * (topVoxels[i] - bottomValue)
                )
            )
        );
    }

    /**
     * Fill the single empty slice {@link curZ} in place from its nearest
     * contoured neighbours and write it back into {@link sdf}. Returns whether
     * a fill was written — false when the slice is already contoured or has no
     * contoured neighbour on either side.
     *
     * With a contoured neighbour on both sides the slice is linearly
     * interpolated between them; with a neighbour on only one side that
     * neighbour's contour is copied onto the slice (extrapolation). Shared by
     * the single-slice apply path and its pre-apply preview ghost so the two
     * cannot diverge.
     */
    private static fillSingleSliceInto(
        sdf: SignedDistanceField,
        curZ: number
    ): boolean {
        const sizeZ = sdf.getAllSlices().length;
        const hasContour = ContourOperations.contourPresenceReader(sdf);
        const position = ContourOperations.locateInContouredRange(
            curZ,
            sizeZ,
            hasContour
        );
        // Only one side holds contour: copy its nearest contour onto the
        // slice (extrapolation).
        if (position.kind === 'beyond') {
            sdf.arrayToTextureSlice(sdf.sliceToArray(position.nearestZ), curZ);
            return true;
        }
        if (position.kind !== 'between') {
            return false;
        }
        // Contour on both sides: linear interpolation between the nearest.
        const first = ContourOperations.firstContouredSlice;
        const bottomZ = first(curZ - 1, 0, -1, hasContour)!;
        const topZ = first(curZ + 1, sizeZ - 1, 1, hasContour)!;
        sdf.arrayToTextureSlice(
            ContourOperations.interpolateSliceVoxels(
                sdf.sliceToArray(bottomZ),
                sdf.sliceToArray(topZ),
                bottomZ,
                topZ,
                curZ
            ),
            curZ
        );
        return true;
    }

    /**
     * Interpolate every empty slice that lies strictly between two contoured
     * slices, writing each back into {@link sdf} in place.
     *
     * The contoured slices are snapshotted up front, so slices filled by this
     * pass are never themselves used as interpolation endpoints. Each gap
     * slice is therefore interpolated from the two fixed original contours —
     * the exact linear interpolant, independent of traversal order and free of
     * the cumulative re-rounding a running in-place fill would introduce.
     * Slices outside the outermost contours are one-sided and left untouched.
     *
     * @returns The filled Z indices, ascending — the exact revisit list for
     * the optional intensity-refinement post-pass.
     */
    private static fillAllEmptySlicesInto(sdf: SignedDistanceField): number[] {
        const totalSlices = sdf.getAllSlices().length;
        const contouredZ: number[] = [];
        const contouredVoxels: Uint8Array[] = [];
        for (let z = 0; z < totalSlices; z++) {
            const sliceVoxels = sdf.sliceToArray(z);
            if (ContourOperations.sliceHasContour(sliceVoxels)) {
                contouredZ.push(z);
                contouredVoxels.push(sliceVoxels);
            }
        }
        // Fill the empty interior of each consecutive pair of contoured slices.
        // No contoured slice lies strictly between a consecutive pair, so every
        // interior index is empty; their union is exactly the empty slices
        // strictly between the first and last contour — the set
        // previewInterpolateAllSlices reports.
        const filledZs: number[] = [];
        for (let k = 0; k + 1 < contouredZ.length; k++) {
            const bottomZ = contouredZ[k];
            const topZ = contouredZ[k + 1];
            for (let curZ = bottomZ + 1; curZ < topZ; curZ++) {
                sdf.arrayToTextureSlice(
                    ContourOperations.interpolateSliceVoxels(
                        contouredVoxels[k],
                        contouredVoxels[k + 1],
                        bottomZ,
                        topZ,
                        curZ
                    ),
                    curZ
                );
                filledZs.push(curZ);
            }
        }
        return filledZs;
    }

    /**
     * Patient-space Z (mm) of the centre of texture slice {@link index}.
     */
    private static sliceCenterZMm(target: RegionOfInterest, index: number) {
        return target.texture!.textureGrid.voxelCenterToWorldSpace(
            vec3.fromValues(0, 0, index)
        )[2];
    }

    /**
     * Describes which slices {@link handleInterpolateAllSlices} would fill,
     * WITHOUT modifying the SDF. The filled set is exactly the empty slices
     * that lie strictly between the lowest and highest contoured slice — for
     * any such slice the write path finds a contoured neighbour both below and
     * above and interpolates it; slices outside that span have a neighbour on
     * only one side and are left untouched. Reads the cached CPU-side pixels
     * (cheap, no GPU readback) so it is safe to call reactively for preview.
     */
    public previewInterpolateAllSlices(
        target: RegionOfInterest
    ): InterpolationPreview {
        const sdf = target.texture?.signedDistanceField;
        if (!target.texture || !sdf) {
            return { status: 'unavailable' };
        }
        const slices = sdf.pixels;
        let firstContoured = -1;
        let lastContoured = -1;
        for (let z = 0; z < slices.length; z++) {
            if (ContourOperations.sliceHasContour(slices[z])) {
                if (firstContoured === -1) {
                    firstContoured = z;
                }
                lastContoured = z;
            }
        }
        // Need two distinct contoured slices to interpolate between.
        if (firstContoured === -1 || firstContoured === lastContoured) {
            return { status: 'nothingToFill' };
        }
        const fillIndices: number[] = [];
        for (let z = firstContoured + 1; z < lastContoured; z++) {
            if (!ContourOperations.sliceHasContour(slices[z])) {
                fillIndices.push(z);
            }
        }
        if (fillIndices.length === 0) {
            return { status: 'nothingToFill' };
        }
        const zA = ContourOperations.sliceCenterZMm(target, fillIndices[0]);
        const zB = ContourOperations.sliceCenterZMm(
            target,
            fillIndices[fillIndices.length - 1]
        );
        return {
            status: 'fills',
            // Every filled slice lies strictly between two contours, so the
            // all-slices pass only ever interpolates (never extrapolates).
            mode: 'interpolate',
            sliceCount: fillIndices.length,
            startZMm: Math.min(zA, zB),
            endZMm: Math.max(zA, zB),
        };
    }

    /**
     * Describes whether {@link handleInterpolateSingleSlice} would fill the
     * slice under the viewed plane, WITHOUT modifying the SDF. Mirrors that
     * method's gating exactly: a plane that is no slice plane, an
     * out-of-range plane, an already-contoured slice, or a slice with no
     * contoured neighbour on either side each report a reason the apply
     * would be a no-op.
     */
    public previewInterpolateSingleSlice(
        target: RegionOfInterest,
        primaryImage: ImageObject,
        plane: PlaneMm
    ): InterpolationPreview {
        const sdf = target.texture?.signedDistanceField;
        if (!target.texture || !sdf) {
            return { status: 'unavailable' };
        }
        const resolved = resolveTextureSlice(
            target.texture,
            primaryImage,
            plane
        );
        if (resolved.kind === 'notASlicePlane') {
            return { status: 'notASlicePlane' };
        }
        if (resolved.kind === 'outsideTexture') {
            return { status: 'outOfRange' };
        }
        const curZ = resolved.sliceZ;
        const slices = sdf.pixels;
        const position = ContourOperations.locateInContouredRange(
            curZ,
            slices.length,
            (z) => ContourOperations.sliceHasContour(slices[z])
        );
        if (position.kind === 'onContour') {
            return { status: 'alreadyContoured' };
        }
        if (position.kind === 'noContour') {
            return { status: 'noNeighbors' };
        }
        const zMm = ContourOperations.sliceCenterZMm(target, curZ);
        return {
            status: 'fills',
            // A neighbour on both sides interpolates; only one side copies
            // (extrapolates) that neighbour's contour onto the viewed slice.
            mode: position.kind === 'between' ? 'interpolate' : 'copy',
            sliceCount: 1,
            startZMm: zMm,
            endZMm: zMm,
        };
    }

    /**
     * Builds a preview texture of the interpolation result WITHOUT touching the
     * live ROI: clones the ROI's texture and runs the exact same fill routine —
     * and, when enabled, the exact same intensity-refinement post-pass — the
     * apply uses on the clone, so the previewed contours are byte-identical
     * to what {@link handleInterpolateAllSlices} / {@link
     * handleInterpolateSingleSlice} would commit in either mode. Returns
     * undefined when there is nothing to fill (the caller should clear any
     * shown preview). The caller owns the returned texture and must dispose it.
     */
    public buildInterpolatedPreviewTexture(
        target: RegionOfInterest,
        scope: 'allEmpty' | 'current',
        primaryImage: ImageObject,
        plane: PlaneMm | null,
        refineOptions: IntensityInterpolationOptions = DISABLED_INTENSITY_INTERPOLATION
    ): ContourTexture | undefined {
        if (scope === 'current' && plane === null) {
            return undefined;
        }
        const preview =
            scope === 'current' && plane !== null
                ? this.previewInterpolateSingleSlice(
                      target,
                      primaryImage,
                      plane
                  )
                : this.previewInterpolateAllSlices(target);
        if (preview.status !== 'fills' || !target.texture) {
            return undefined;
        }
        const clone = target.texture.clone();
        const cloneSdf = clone.signedDistanceField;
        let filledZs: number[];
        if (scope === 'current' && plane !== null) {
            // target and clone share an identical texture grid, so resolving
            // against the source is valid for the clone.
            const resolved = resolveTextureSlice(
                target.texture,
                primaryImage,
                plane
            );
            filledZs =
                resolved.kind === 'slice' &&
                ContourOperations.fillSingleSliceInto(cloneSdf, resolved.sliceZ)
                    ? [resolved.sliceZ]
                    : [];
        } else {
            filledZs = ContourOperations.fillAllEmptySlicesInto(cloneSdf);
        }
        // Refine the clone's filled slices exactly as the apply paths do, so
        // the ghost never shows a shape-only contour that an image-guided
        // apply would then commit differently. `target` supplies only the
        // texture grid and SDF encoding, which the clone shares, so refining
        // the clone's bytes against it is exact.
        this.applyRefinementPass(
            cloneSdf,
            target,
            primaryImage,
            filledZs,
            refineOptions
        );
        return clone;
    }
}
